import { getWarrantyDaysRemaining } from '../../../../../../helpers/getWarrantyDaysRemaining';
import type { Drone } from '../../../../../../types';

import { hectaresToAcres } from '@@utils/areaUtil';
import { formatToUSDate } from '@@utils/dateTimeUtils/dateFormatUtil';

type SpecLabels = {
    dronePrefix: string;
    emDash: string;
    faaPrefix: string;
    firmwareVersion: string;
    ids: string;
    unitAcres: string;
    unitDays: string;
    unitFlights: string;
    unitHoursShort: string;
    usage: string;
    warranty: string;
    warrantyExpPrefix: string;
    warrantyExpired: string;
};

export type Spec = { label: string; value: string };

export type DroneSpecs = {
    usage: Spec;
    warranty: Spec;
    ids: Spec;
    firmware: Spec;
};

export const getDroneSpecs = ({
    drone,
    labels,
    now
}: {
    drone: Drone;
    labels: SpecLabels;
    now: Date;
}): DroneSpecs => {
    const { emDash } = labels;

    const acres = Math.round(hectaresToAcres(drone.flownHectares)).toLocaleString();
    const usageValue = `${drone.flightHours}${labels.unitHoursShort} · ${drone.flights} ${labels.unitFlights} · ${acres} ${labels.unitAcres}`;

    const getWarrantyValue = (): string => {
        if (!drone.warranty) {
            return emDash;
        }

        const daysRemaining = getWarrantyDaysRemaining({ end: drone.warranty.end, now });

        if (daysRemaining <= 0) {
            return labels.warrantyExpired;
        }

        const expDate = formatToUSDate(new Date(drone.warranty.end));

        return `${daysRemaining} ${labels.unitDays} · ${labels.warrantyExpPrefix} ${expDate}`;
    };

    const faaValue = drone.faaRegistrationNumber ?? emDash;
    const idsValue = `${labels.faaPrefix} ${faaValue} · ${labels.dronePrefix} ${drone.serial}`;

    return {
        firmware: { label: labels.firmwareVersion, value: drone.firmwareVersion },
        ids: { label: labels.ids, value: idsValue },
        usage: { label: labels.usage, value: usageValue },
        warranty: { label: labels.warranty, value: getWarrantyValue() }
    };
};
